import { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { rosterApi } from '../services/api';
import { utcToSydLocalInput, sydLocalInputToUtcIso } from '../utils/sydneyTime';
import { toast } from 'sonner';
import { UserCheck, UserX, Ban, AlertTriangle, Loader2 } from 'lucide-react';

const OPTIONS = [
  { value: 'attended', label: 'Worker attended — forgot to clock in', hint: 'Logs approved manual hours and marks the shift completed', Icon: UserCheck, color: 'border-emerald-300 bg-emerald-50 text-emerald-800' },
  { value: 'missed', label: 'Genuinely missed', hint: 'Keeps the missed flag, records the reason', Icon: AlertTriangle, color: 'border-rose-300 bg-rose-50 text-rose-800' },
  { value: 'cancelled_by_participant', label: 'Cancelled by participant', hint: 'Shift marked cancelled, no hours', Icon: Ban, color: 'border-slate-300 bg-slate-50 text-slate-700' },
  { value: 'worker_unavailable', label: 'Worker unavailable / sick', hint: 'Shift marked cancelled, no hours', Icon: UserX, color: 'border-amber-300 bg-amber-50 text-amber-800' },
];

const fmt = (iso) => new Date(iso).toLocaleString('en-AU', { timeZone: 'Australia/Sydney', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/** Supervisor closes out a missed / un-clocked roster shift with an auto-note. */
export function ResolveShiftDialog({ shift, open, onOpenChange, onResolved }) {
  const [resolution, setResolution] = useState('attended');
  const [reason, setReason] = useState('');
  const [times, setTimes] = useState({ clock_in: '', clock_out: '', break_minutes: 0 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !shift) return;
    setResolution('attended'); setReason('');
    setTimes({ clock_in: utcToSydLocalInput(shift.start_at), clock_out: utcToSydLocalInput(shift.end_at), break_minutes: shift.break_minutes || 0 });
  }, [open, shift]);

  if (!shift) return null;

  const submit = async () => {
    if (!reason.trim()) { toast.error('Please add a reason — it becomes the audit note'); return; }
    setSaving(true);
    try {
      const body = { resolution, reason: reason.trim() };
      if (resolution === 'attended') {
        if (times.clock_out <= times.clock_in) { toast.error('End must be after start'); setSaving(false); return; }
        body.clock_in = sydLocalInputToUtcIso(times.clock_in);
        body.clock_out = sydLocalInputToUtcIso(times.clock_out);
        body.break_minutes = parseInt(times.break_minutes) || 0;
      }
      const { data } = await rosterApi.resolveShift(shift.id, body);
      toast.success(resolution === 'attended' ? `Resolved — ${data.manual_entry?.total_hours}h logged for ${shift.worker_name}` : 'Shift resolved and note saved');
      onOpenChange(false);
      onResolved?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not resolve shift');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="resolve-shift-dialog">
        <DialogHeader>
          <DialogTitle>Resolve shift</DialogTitle>
          <DialogDescription>
            <strong>{shift.worker_name || 'Open shift'}</strong> · {fmt(shift.start_at)} → {new Date(shift.end_at).toLocaleTimeString('en-AU', { timeZone: 'Australia/Sydney', hour: '2-digit', minute: '2-digit' })}
            {' '}with {shift.client_names?.join(', ') || shift.client_name || '—'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid gap-2">
            {OPTIONS.filter(o => shift.worker_id || o.value !== 'attended').map(({ value, label, hint, Icon, color }) => (
              <button key={value} type="button" onClick={() => setResolution(value)}
                className={`flex items-start gap-3 rounded-lg border p-3 text-left transition-all ${resolution === value ? `${color} ring-2 ring-teal-500 ring-offset-1` : 'border-slate-200 bg-white hover:bg-slate-50'}`}
                data-testid={`resolve-option-${value}`}>
                <Icon className="h-4 w-4 mt-0.5 shrink-0" />
                <span><span className="block text-sm font-medium">{label}</span><span className="block text-xs opacity-80">{hint}</span></span>
              </button>
            ))}
          </div>
          {resolution === 'attended' && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Actual start</Label><Input type="datetime-local" value={times.clock_in} onChange={(e) => setTimes({ ...times, clock_in: e.target.value })} className="mt-1 h-9" data-testid="resolve-start" /></div>
              <div><Label className="text-xs">Actual end</Label><Input type="datetime-local" value={times.clock_out} onChange={(e) => setTimes({ ...times, clock_out: e.target.value })} className="mt-1 h-9" data-testid="resolve-end" /></div>
              <div className="col-span-2"><Label className="text-xs">Unpaid break (min)</Label><Input type="number" min={0} value={times.break_minutes} onChange={(e) => setTimes({ ...times, break_minutes: e.target.value })} className="mt-1 h-9 w-32" /></div>
            </div>
          )}
          <div>
            <Label className="text-sm">Reason / note <span className="text-rose-500">*</span></Label>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" placeholder="What happened? This is saved as a timestamped audit note on the shift." data-testid="resolve-reason" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="resolve-submit">
              {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</> : 'Resolve shift'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
